<template>
  <tr ref="cls" :class="{ 'blue-grey darken-2': processed }">
    <td colspan="7">
      <v-form ref="form">
        <v-row align="center" dense class="my-0">
          <v-col cols="1">
            <v-btn
              icon
              small
              class="v-data-table__expand-icon ml-n3 ml-xl-0"
              :class="{ 'v-data-table__expand-icon--active': isExpanded }"
              @click="$emit('expand', !isExpanded)"
            >
              <v-icon>mdi-chevron-down</v-icon>
            </v-btn>
            <v-btn :href="item.media_url" target="_blank" icon small>
              <v-icon>mdi-video</v-icon>
            </v-btn>
            <v-btn :href="item.site_urls[0]" target="_blank" icon small>
              <v-icon>mdi-application</v-icon>
            </v-btn>
          </v-col>
          <v-col v-placeholder cols="3" class="text-truncate pt-3">
            <span v-if="item.site_urls[0]">{{ item.site_urls[0] | truncate(100) }}</span>
            <v-row justify="space-between" no-gutters class="mt-2">
              <nv-show cols="auto" header="Last seen on">
                <span>{{ item.last_seen_on | date }}</span>
              </nv-show>
              <nv-show cols="auto" header="Countries">
                <span v-for="(c, i) in item.countries" :key="i">
                  {{ c | uppercase }}
                  <span v-if="i < item.countries.length - 1" class="mr-1">,</span>
                </span>
              </nv-show>
            </v-row>
          </v-col>
          <v-col cols="3">
            <v-row no-gutters align="center">
              <v-col>
                <v-textarea
                  ref="pattern"
                  v-model="model.pattern"
                  :rules="rules.pattern"
                  :disabled="processed || loading"
                  rows="1"
                  auto-grow
                  label="Pattern"
                  hide-details="auto"
                  solo
                  flat
                />
              </v-col>
              <ai-button
                :loading="aiLoading"
                :disabled="processed || loading"
                :can-view="canView()"
                :is-v2="isV2()"
                @generate="generatePatternWithMav"
                @generate-direct="generatePatternDirectUpgrade"
              />
            </v-row>
          </v-col>
          <v-col :cols="model.effective_start_date ? 2 : 2">
            <v-row no-gutters align="center">
              <v-col>
                <hierarchy-discovery
                  v-model="model.tree_id"
                  :rules="rules.treeId"
                  :disabled="processed || loading"
                  :country="'*'"
                />
              </v-col>
              <v-col v-if="canViewTreePrediction()" cols="auto">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      class="ai-tree-button ml-2"
                      :loading="treeAiLoading"
                      :disabled="processed || loading"
                      v-bind="attrs"
                      v-on="on"
                      @click="generateTreeIdWithMav"
                    >
                      <v-icon small color="white">mdi-robot</v-icon>
                    </v-btn>
                  </template>
                  <span>Predict Tree ID</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-col>
          <v-col :cols="model.effective_start_date ? 3 : 2">
            <v-row dense align="center" class="flex-nowrap">
              <v-col>
                <v-select
                  ref="property"
                  v-model="model.property"
                  :items="videoProperties"
                  :rules="rules.property"
                  :disabled="processed || loading"
                  placeholder="Property"
                  solo
                  flat
                  hide-details="auto"
                />
              </v-col>
              <nv-show
                :header="model.effective_start_date ? 'Start Date' : ''"
                :class="{ 'mt-2': !model.effective_start_date }"
                cols="auto"
                no-placeholder
                class="pl-lg-2"
              >
                <plus-icon-button :disabled="isAddDisabled" @add="addLocalization" />
                <owl-date-picker
                  v-model="model.effective_start_date"
                  :disabled="processed || loading"
                  label="Start Date"
                  icon
                  clearable
                />
                <span v-if="model.effective_start_date">
                  {{ model.effective_start_date }}
                </span>
              </nv-show>
              <v-col v-if="lockedBy === $auth.user.email" cols="auto">
                <template v-if="onTime(lockExpiresAt)">
                  <save-btn
                    v-if="!processed"
                    :loading="loading"
                    save-text="Test"
                    class="ml-lg-4"
                    @click="testPatternDialog.show = true"
                  />
                  <done-chip v-else />
                </template>
                <expire-chip v-else />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row
          v-for="(row, index) in localizations.slice(0, Math.max(1, countries.length - 1))"
          :key="index"
          align="center"
          class="my-0 "
        >
          <v-col cols="4" lg="5">
            <hierarchy-discovery
              v-model="row.tree_id"
              :rules="rules.treeId"
              :disabled="processed || loading"
              :country="getCountry(row.country)"
            />
          </v-col>
          <v-col cols="2">
            <v-select
              v-model="row.country"
              :items="filteredCountries"
              label="Country"
              :disabled="processed || loading"
            ></v-select>
          </v-col>
          <v-col cols="1">
            <owl-date-picker
              v-model="model.effective_start_date"
              :disabled="processed || loading"
              label="Start Date"
              icon
              clearable
            />
          </v-col>

          <v-col cols="1">
            <v-btn icon :disabled="processed || loading" @click="removeLocalization(index)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
      <test-pattern-dialog
        v-model="testPatternDialog.show"
        :domain="item.domain"
        :pattern="model.pattern"
        product="video"
        @saved="save"
      />
      <pattern-comparison-dialog
        v-model="patternComparisonDialog.show"
        :original-pattern="model.pattern"
        :ai-generated-pattern="aiGeneratedPattern"
        :confidence-score="confidenceScore"
        :domain="item.domain"
        product="video"
        @use-ai-pattern="useAiPatternAndSendFeedback()"
        @keep-original="keepOriginalAndSendFeedback()"
        @cancel="patternComparisonDialog.show = false"
      />
    </td>
  </tr>
</template>
<script>
import HTTP from '@/api/ant';
import MAV from '@/api/mav';
import Rules from 'bsm-validation-rules';
import { enumFetch } from '@nielsen-media/ui-toolkit-vue-api';
import ClassifyRowMixin from '@/components/cls/ClassifyRowMixin';
import OwlDatePicker from '@/components/OwlDatePicker';
import HierarchyDiscovery from '@/components/cls/video/HierarchyDiscovery';
import TestPatternDialog from '@/components/cls/TestPatternDialog';
import SaveBtn from '@/components/cls/SaveBtn.vue';
import DoneChip from '@/components/cls/DoneChip.vue';
import ExpireChip from '@/components/cls/ExpireChip.vue';
import PlusIconButton from '@/components/PlusIconButton.vue';
import AiButton from '@/components/cls/AIButton.vue';
import PatternComparisonDialog from '@/components/cls/PatternComparisonDialog.vue';
import { getConfigMap } from '@/helpers/filters';
import { submitFeedback } from '@/api/feedback';

const rules = {
  pattern: [
    Rules.presence(),
    Rules.format(/^[^\r\n]*$/, 'should not include a new line character'),
  ],
  property: [Rules.presence()],
  treeId: [Rules.presence()],
};

export default {
  components: {
    'ai-button': AiButton,
    ExpireChip,
    DoneChip,
    SaveBtn,
    TestPatternDialog,
    PatternComparisonDialog,
    HierarchyDiscovery,
    OwlDatePicker,
    PlusIconButton,
  },
  mixins: [ClassifyRowMixin],
  props: {
    recentItems: {
      type: Array,
      default: () => [],
    },
    isExpanded: {
      type: Boolean,
      required: true,
    },
    lockedBy: {
      type: String,
      default: '',
    },
    lockExpiresAt: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      model: {
        pattern: this.item.media_url.replace(/^\w*:\/\//, ''),
        tree_id: null,
        property: 'valid',
        effective_start_date: null,
      },
      rules: {
        pattern: [],
        property: [],
        treeId: [],
      },
      videoProperties: [],
      testPatternDialog: { show: false },
      patternComparisonDialog: { show: false },
      aiGeneratedPattern: '',
      aiSuggestionFull: null,
      confidenceScore: 0.8,
      feedbackSent: false,
      localizations: [],
      countries: [
        { text: 'default', value: '*' },
        { text: 'us', value: 'us' },
      ],
      payloadArray: [],
      formattedPayload: null,
      aiLoading: false,
      treeAiLoading: false,
      aiGeneratedTreeId: null,
      aiTreeSuggestionFull: null,
      treeConfidenceScore: 0.8,
      treeFeedbackSent: false,
    };
  },
  computed: {
    filteredCountries() {
      return this.countries.filter(country => country.value !== '*');
    },
    getCountry() {
      return rowCountry => rowCountry || 'us';
    },
    isAddDisabled() {
      return (
        this.processed || this.loading || this.localizations.length >= this.filteredCountries.length
      );
    },
  },
  watch: {
    'item.media_url': function(v) {
      if (!this.processed) {
        this.model.pattern = v.replace(/^\w*:\/\//, '');
      }
    },
  },
  created() {
    this.enumFetch(HTTP, {
      videoProperties: '/video/rules/enum/properties',
    });
  },
  methods: {
    // Original sendFeedback method (copied from prompt)
    async sendFeedback(action, finalPattern) {
      try {
        await submitFeedback({
          input_string: this.item.media_url,
          ai_suggestion: {
            pattern: this.aiSuggestionFull?.pattern || this.aiGeneratedPattern,
            rationale: this.aiSuggestionFull?.rationale || '',
            confidence: this.aiSuggestionFull?.confidence || this.confidenceScore,
            entity_type: 'video',
          },
          user_final_decision: {
            pattern: finalPattern,
            entity_type: 'video',
          },
          action,
        });
      } catch (error) {
        console.error('Failed to submit feedback:', error);
      }
    },

    async sendTreeFeedback(action, finalTreeId) {
      try {
        await submitFeedback({
          input_string: `${this.item.site_urls?.[0] || ''} | ${this.item.domain || ''}`,
          ai_suggestion: {
            tree_id: this.aiTreeSuggestionFull?.tree_id || this.aiGeneratedTreeId,
            rationale: this.aiTreeSuggestionFull?.rationale || '',
            confidence: this.aiTreeSuggestionFull?.confidence || this.treeConfidenceScore,
            entity_type: 'tree_prediction',
          },
          user_final_decision: {
            tree_id: finalTreeId,
            entity_type: 'tree_prediction',
          },
          action,
        });
      } catch (error) {
        console.error('Failed to submit tree feedback:', error);
      }
    },

    // 1. Case: Direct Apply (Version 1)
    async generatePatternWithMav() {
      this.aiLoading = true;
      try {
        const response = await this.callMavAPI(this.item.media_url);

        this.aiGeneratedPattern = response.pattern;
        this.aiSuggestionFull = response;
        this.model.pattern = response.pattern;
      } catch (error) {
        console.error('AI pattern generation failed:', error);
        this.$flash('error', 'Failed to generate pattern with MAV AI');
      } finally {
        this.aiLoading = false;
      }
    },

    // 2. Case: Through Dialogue Box (Version 2) - This just opens the dialog
    async generatePatternDirectUpgrade() {
      this.aiLoading = true;
      try {
        const response = await this.callMavAPI(this.item.media_url);
        this.aiGeneratedPattern = response.pattern;
        this.aiSuggestionFull = response;
        this.confidenceScore = response.confidence || 0;
        this.patternComparisonDialog.show = true;
      } catch (error) {
        console.error('AI pattern generation failed:', error);
        this.$flash('error', 'Failed to generate pattern with MAV AI');
      } finally {
        this.aiLoading = false;
      }
    },

    // Handlers for Dialogue Box (Version 2) actions

    useAiPatternAndSendFeedback() {
      this.model.pattern = this.aiGeneratedPattern;
      this.sendFeedback('ACCEPTED', this.aiGeneratedPattern);
      this.feedbackSent = true;
      this.patternComparisonDialog.show = false;
    },

    keepOriginalAndSendFeedback() {
      this.sendFeedback('CORRECTED', this.model.pattern);
      this.feedbackSent = true;
      this.patternComparisonDialog.show = false;
    },

    // Tree ID Prediction Methods

    async generateTreeIdWithMav() {
      this.treeAiLoading = true;
      try {
        const response = await this.callMavTreeAPI();
        this.aiGeneratedTreeId = response.tree_id;
        this.aiTreeSuggestionFull = response;
        this.model.tree_id = response.tree_id;
        this.$flash('success', `Tree ID predicted: ${response.tree_id}`);
      } catch (error) {
        console.error('AI tree ID prediction failed:', error);
        this.$flash('error', 'Failed to predict tree ID with MAV AI');
      } finally {
        this.treeAiLoading = false;
      }
    },

    async generateTreeIdDirectUpgrade() {
      this.treeAiLoading = true;
      try {
        const response = await this.callMavTreeAPI();
        this.aiGeneratedTreeId = response.tree_id;
        this.aiTreeSuggestionFull = response;
        this.treeConfidenceScore = response.confidence || 0;
        // Show tree ID prediction in a dialog or directly apply
        this.model.tree_id = response.tree_id;
        this.$flash(
          'success',
          `Tree ID predicted: ${response.tree_id} (confidence: ${(
            this.treeConfidenceScore * 100
          ).toFixed(1)}%)`,
        );
      } catch (error) {
        console.error('AI tree ID prediction failed:', error);
        this.$flash('error', 'Failed to predict tree ID with MAV AI');
      } finally {
        this.treeAiLoading = false;
      }
    },

    async callMavTreeAPI() {
      try {
        const response = await MAV.post('/predict/video', {
          site_url: this.item.site_urls?.[0] || '',
          domain: this.item.domain || '',
        });
        // Extract prediction data and map to expected format
        const prediction = response.data.prediction || response.data;

        // Implement hierarchy fallback: channel -> brand -> parent
        let tree_id = null;
        let confidence = prediction.overall_confidence;
        let hierarchyLevel = '';
        let hierarchyName = '';

        if (prediction.channel_id && prediction.channel_id !== 0) {
          tree_id = prediction.channel_id;
          confidence = prediction.channel_confidence;
          hierarchyLevel = 'Channel';
          hierarchyName = prediction.channel_name;
        } else if (prediction.brand_id && prediction.brand_id !== 0) {
          tree_id = prediction.brand_id;
          confidence = prediction.brand_confidence;
          hierarchyLevel = 'Brand';
          hierarchyName = prediction.brand_name;
        } else if (prediction.parent_id && prediction.parent_id !== 0) {
          tree_id = prediction.parent_id;
          confidence = prediction.parent_confidence;
          hierarchyLevel = 'Parent';
          hierarchyName = prediction.parent_name;
        }

        return {
          tree_id: tree_id,
          confidence: confidence,
          rationale: `Predicted ${hierarchyLevel}: ${hierarchyName} (Parent: ${
            prediction.parent_name
          }, Brand: ${prediction.brand_name || 'N/A'}, Channel: ${prediction.channel_name ||
            'N/A'})`,
          channel_id: prediction.channel_id,
          channel_name: prediction.channel_name,
          brand_id: prediction.brand_id,
          brand_name: prediction.brand_name,
          parent_id: prediction.parent_id,
          parent_name: prediction.parent_name,
          hierarchy_level: hierarchyLevel,
        };
      } catch (error) {
        console.error('MAV Tree API error:', error);
        throw error;
      }
    },

    // --- Existing utility methods below ---

    async callMavAPI(mediaUrl, expectedPattern = null) {
      try {
        if (this.isShadowMode() && expectedPattern) {
          const response = await MAV.post('/shadow/patterns/video', {
            media_url: mediaUrl,
            site_url: this.item.site_urls?.[0] || '',
            domain: this.item.domain || '',
            expected_pattern: expectedPattern,
          });
          return response.data.ai_suggestion || response.data;
        }

        const response = await MAV.post('/patterns/video/generate', {
          media_url: mediaUrl,
          site_url: this.item.site_urls?.[0] || '',
          domain: this.item.domain || '',
        });
        return response.data;
      } catch (error) {
        console.error('MAV API error:', error);
        throw error;
      }
    },
    async sendShadowEvaluation(mediaUrl, expectedPattern) {
      try {
        await MAV.post('/shadow/patterns/video', {
          media_url: mediaUrl,
          site_url: this.item.site_urls?.[0] || '',
          domain: this.item.domain || '',
          expected_pattern: expectedPattern,
        });
      } catch (error) {
        console.error('Shadow API error:', error);
      }
    },
    canView() {
      const config = this.getConfigMap();
      const mavConfig = config['mavFeature_owl_ui'];
      return mavConfig?.video_enabled === true || false;
    },
    isV2() {
      const config = this.getConfigMap();
      return config['mavFeature_owl_ui']?.enabled?.v2;
    },
    canViewTreePrediction() {
      const config = this.getConfigMap();
      return config['mavFeature_owl_ui']?.video_enabled === true || false;
    },
    isShadowMode() {
      const config = this.getConfigMap();
      const mavConfig = config['mavFeature_owl_ui'];
      return (
        mavConfig?.enabled?.shadow === true && !mavConfig?.enabled?.v1 && !mavConfig?.enabled?.v2
      );
    },
    isProductionMode() {
      const config = this.getConfigMap();
      const mavConfig = config['mavFeature_owl_ui'];
      return (mavConfig?.enabled?.v1 || mavConfig?.enabled?.v2) && !mavConfig?.enabled?.shadow;
    },
    save() {
      this.createPayloadArray();
      this.rules = rules;

      const originalPattern = this.item.media_url.replace(/^\w*:\/\//, '');
      if (this.aiSuggestionFull && this.model.pattern !== originalPattern) {
        if (this.isProductionMode() && !this.feedbackSent) {
          const action =
            this.aiSuggestionFull.pattern === this.model.pattern ? 'ACCEPTED' : 'CORRECTED';
          this.sendFeedback(action, this.model.pattern);
        }
      }
      this.feedbackSent = false;

      // Send tree ID feedback if AI suggestion was used
      if (this.aiTreeSuggestionFull && !this.treeFeedbackSent) {
        if (this.isProductionMode()) {
          const treeAction =
            this.aiTreeSuggestionFull.tree_id === this.model.tree_id ? 'ACCEPTED' : 'CORRECTED';
          this.sendTreeFeedback(treeAction, this.model.tree_id);
        }
      }
      this.treeFeedbackSent = false;

      if (this.isShadowMode()) {
        try {
          this.sendShadowEvaluation(this.item.media_url, this.model.pattern);
        } catch (error) {
          console.error('shadow error:', error);
        }
      }

      this.submitClassify(`/video/discoveries/${this.item.id}/multiplerules`, {
        success: () => {
          this.$emit('saved');
        },
      });
    },
    enumFetch,
    addLocalization() {
      this.localizations.push({
        tree_id: null,
        country: '',
      });
    },

    removeLocalization(index) {
      this.localizations.splice(index, 1);
    },
    createPayloadArray() {
      const baseObject = {
        pattern: this.model.pattern,
        property: this.model.property,
        effective_start_date: this.model.effective_start_date,
      };

      this.payloadArray = [
        {
          ...baseObject,
          tree_id: this.model.tree_id,
          localization: '*',
        },
      ];

      this.localizations.forEach(localization => {
        this.payloadArray.push({
          ...baseObject,
          tree_id: localization.tree_id,
          localization: localization.country,
        });
      });
      this.formattedPayload = { rules: this.payloadArray };
      return this.payloadArray;
    },
    getConfigMap,
  },
};
</script>

<style scoped>
.position-relative {
  position: relative;
}

.ai-button {
  position: absolute;
  right: 4px;
  top: 8px;
  z-index: 1;

  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3) !important;
  transition: all 0.3s ease !important;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important;
    box-shadow: 0 6px 12px rgba(102, 126, 234, 0.5) !important;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
  }
}

.ai-tree-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3) !important;
  transition: all 0.3s ease !important;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important;
    box-shadow: 0 6px 12px rgba(102, 126, 234, 0.5) !important;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
  }
}
</style>
